import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AppColors, config, typographyClamps } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import GetStartedModel from './models/GetStartedModel';
import BookDemoFormModel from './models/BookDemoFormModel';
import { IsNotNullOrEmpty } from '../utils/valid';
import { getItem } from '../utils/localStorage';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GradientButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  width: 'fit-content',
  height: 42,
  background: AppColors.aiGradient3,
  borderRadius: '12px',
  border: 0,
  color: 'white',
  padding: '10px 32px',
  minWidth: 0, // default for xs
  // Responsive override for md and up
  [theme.breakpoints.up('md')]: {
    minWidth: '170px',
  },
  textTransform: 'none',
  fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
  fontWeight: 500,
  boxShadow: 'none',
  textAlign: 'center',
  transition: '0.3s',
  '&:hover': {
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.3)',
    background: AppColors.aiGradient3,
  },
}));

export const navItems = [
  { label: 'Home', path: '/' },
  { label: 'The Lot', path: '/the-lot' },
  { label: 'The Web', path: '/the-web' },
  // { label: 'How it Works', path: '/how-it-works' },
  // { label: 'Features', path: '/features' },
  // { label: 'Benefits', path: '/benefits' },
  // { label: 'Pricing', path: '/pricing' },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);

  const {
    toggle: toggleGetStartedModel,
    // onOpen: onOpenGetStartedModel,
    onClose: onCloseGetStartedModel,
  } = useToggle();

  const {
    toggle: toggleRequestDemoModel,
    onOpen: onOpenRequestDemoModel,
    onClose: onCloseRequestDemoModel,
  } = useToggle();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigateToLogin = () => {
    //const loginUrl = `https://app.qrbuddy.ai`;

    let loginUrl = `${config.WEB_URL}`;
    const storedReferralCode = getItem(config.REFERRAL_CODE_PARAM_KEY);
    if (IsNotNullOrEmpty(storedReferralCode)) {
      loginUrl += `?${config.REFERRAL_CODE_PARAM_KEY}=${storedReferralCode}`;
    }

    window.open(`${loginUrl}`) || window.location.replace(`${loginUrl}`);
  };

  const handleGetStartedClick = () => {
    navigate('/pricing');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRequestDemoClick = () => {
    onOpenRequestDemoModel();
  };

  return (
    <>
      <AppBar
        position='sticky'
        sx={{
          // background: AppColors.headerGradient,
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{ bgcolor: AppColors.vividBlueColor2, p: 1, textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: typographyClamps.fontSize16 }}>
            Ask about our UNLIMITED Program... Limited Time
          </Typography>
        </Box>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box
            component={Link}
            to='/'
            // to='https://knocard.com'
            // target='_blank'
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Box
              component='img'
              src='/static/images/knocard_ai_smile_logo.png'
              // src='/static/images/qr_buddy_logo_with_text.png'
              alt='QR Buddy Logo'
              sx={{ height: { xs: 30, md: 40 } }}
            />
            <Box
              component='img'
              src='/static/images/qr_buddy_text_logo.png'
              alt='QR Buddy'
              sx={{
                height: { xs: 15, md: 20 },
                pl: 1,
                pt: 1,
                display: { xs: 'none', sm: 'flex' },
              }}
            />
          </Box>

          {/* Right side (Sign in + Mobile menu) */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Desktop Menu */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              {navItems
                .filter((item) => item.path !== location.pathname)
                .map((item) => (
                  <Button
                    key={item.label}
                    component={NavLink}
                    to={item.path}
                    sx={{
                      // color: AppColors.blueTextColor,
                      color: AppColors.blackColor,
                      fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
                      textTransform: 'none',
                      '&.active': {
                        fontWeight: 'bold',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
            </Box>

            <Button
              variant='text'
              sx={{
                ml: { xs: 0, sm: 2 },
                // color: AppColors.blueTextColor,
                color: AppColors.blackColor,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
              }}
              onClick={navigateToLogin}
            >
              Sign In
            </Button>

            <Button
              variant='contained'
              sx={{
                ml: { xs: 0, sm: 3 },
                width: 'fit-content',
                height: 42,
                borderRadius: 0, //'12px',
                border: 0,
                color: 'white',
                padding: '10px 24px',
                backgroundColor: AppColors.vividBlueColor2,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
                lineHeight: 1.5,
              }}
              onClick={handleRequestDemoClick}
            >
              Book a Demo
            </Button>

            <Button
              variant='contained'
              sx={{
                ml: { xs: 1, sm: 3 },
                width: 'fit-content',
                height: 42,
                borderRadius: 0, //'12px',
                border: 0,
                color: 'white',
                padding: '10px 24px',
                backgroundColor: AppColors.vividBlueColor2,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
                lineHeight: 1.5,
              }}
              onClick={handleGetStartedClick}
            >
              Get Started
            </Button>

            {/* <GradientButton
              variant='contained'
              sx={{
                ml: 2,
              }}
              onClick={() => {
                handleGetStartedClick();
              }}
            >
              Get Started
            </GradientButton> */}

            {/* <GradientButton
              variant='contained'
              onClick={() => {
                onOpenBookDemoForm();
              }}
              sx={{
                ml: 1,
              }}
            >
              Book Demo
            </GradientButton> */}

            {/* Mobile Menu Button */}
            <IconButton
              aria-label='menu'
              sx={{
                display: { xs: 'flex', md: 'none' },
                // backgroundColor: 'white',
                color: 'black',
                borderRadius: '12px',
                ml: 1,
                height: 36, // match Sign In button height
                width: 36, // make it a square
              }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>

            {/* Drawer for Mobile */}
            <Drawer
              anchor='right'
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
            >
              <List sx={{ width: 200 }}>
                {navItems
                  .filter((item) => item.path !== location.pathname)
                  .map((item) => (
                    <ListItem key={item.label} disablePadding>
                      <ListItemButton
                        component={NavLink}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          color: AppColors.menuColor,
                          fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
                          '&.active': {
                            fontWeight: 'bold',
                          },
                        }}
                      >
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
            </Drawer>
          </Box>
        </Toolbar>
      </AppBar>

      <GetStartedModel
        onClose={onCloseGetStartedModel}
        toggle={toggleGetStartedModel}
        onSubmitSuccess={() => {}}
      />

      <BookDemoFormModel
        onClose={onCloseRequestDemoModel}
        toggle={toggleRequestDemoModel}
        onSubmitSuccess={() => {}}
      />

      {/* <DemoRequestDialog
        onClose={onCloseRequestDemoModel}
        toggle={toggleRequestDemoModel}
        onSubmitSuccess={() => {}}
      /> */}
    </>
  );
};

export default Header;
